import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL, giftAPI, RussiaRedirectDiagnostics } from '../services/api';

// Seven quick taps on the app title open a read-only RU/VPN routing diagnostic.
const TAPS_TO_OPEN = 7;
const TAP_WINDOW_MS = 3_000;

const valueText = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'да' : 'нет';
  return String(value);
};

const Row: React.FC<{ label: string; value: unknown; color?: string }> = ({ label, value, color }) => (
  <React.Fragment>
    <span style={{ color: '#6b7280' }}>{label}</span>
    <span style={{ color, wordBreak: 'break-all' }}>{valueText(value)}</span>
  </React.Fragment>
);

export const AdDebugPanel: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [diagnostics, setDiagnostics] = useState<RussiaRedirectDiagnostics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const telegramLanguage = window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code || null;

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await giftAPI.getRussiaDiagnostics();
      setDiagnostics(response.diagnostics);
    } catch (requestError: any) {
      const status = requestError?.response?.status;
      const code = requestError?.response?.data?.error || requestError?.message || 'UNKNOWN_ERROR';
      setError(status ? `HTTP ${status}: ${code}` : String(code));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let taps = 0;
    let firstTapAt = 0;

    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target?.closest?.('.app-title')) return;

      const now = Date.now();
      if (now - firstTapAt > TAP_WINDOW_MS) {
        taps = 0;
        firstTapAt = now;
      }
      taps += 1;
      if (taps >= TAPS_TO_OPEN) {
        taps = 0;
        setIsOpen(true);
      }
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  useEffect(() => {
    if (isOpen) void refresh();
  }, [isOpen, refresh]);

  const copy = useCallback(async () => {
    const text = JSON.stringify({ apiBaseUrl: API_BASE_URL, telegramLanguage, diagnostics, error }, null, 2);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const area = document.createElement('textarea');
      area.value = text;
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      try {
        document.execCommand('copy');
      } catch {
        // The values remain visible in the panel if clipboard access is unavailable.
      }
      area.remove();
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1_500);
  }, [diagnostics, error, telegramLanguage]);

  if (!isOpen) return null;

  const target = diagnostics?.classification.eligible === true;
  const geo = diagnostics?.geo;
  const classification = diagnostics?.classification;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 100000,
        background: 'rgba(6,10,18,0.96)', color: '#e5e7eb',
        display: 'flex', flexDirection: 'column',
        fontFamily: 'Menlo, Consolas, monospace', fontSize: 11,
      }}
    >
      <div style={{ padding: '10px 12px', borderBottom: '1px solid #1f2937', flex: '0 0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <strong style={{ fontSize: 13 }}>Диагностика RU-фильтра</strong>
          {diagnostics && (
            <span
              style={{
                marginLeft: 'auto', padding: '2px 8px', borderRadius: 999,
                background: target ? 'rgba(74,222,128,0.15)' : 'rgba(251,191,36,0.15)',
                color: target ? '#4ade80' : '#fbbf24',
              }}
            >
              {target ? 'RU / VPN' : 'обычный пользователь'}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
          <button onClick={copy} disabled={!diagnostics && !error} style={buttonStyle}>
            {copied ? 'Скопировано' : 'Копировать'}
          </button>
          <button onClick={() => void refresh()} disabled={loading} style={buttonStyle}>
            {loading ? 'Проверяю…' : 'Обновить'}
          </button>
          <button onClick={() => setIsOpen(false)} style={buttonStyle}>Закрыть</button>
        </div>
      </div>

      <div style={{ flex: '1 1 auto', overflowY: 'auto', padding: '10px 12px', WebkitOverflowScrolling: 'touch' }}>
        {loading && !diagnostics && <div style={{ color: '#93c5fd' }}>Определяю язык, IP и категорию…</div>}
        {error && <div style={{ color: '#f87171', marginBottom: 10 }}>Ошибка: {error}</div>}

        {diagnostics && (
          <>
            <Section title="Пользователь">
              <Row label="проверено" value={diagnostics.checkedAt} />
              <Row label="API" value={API_BASE_URL} />
              <Row label="Telegram ID" value={diagnostics.user.telegramId} />
              <Row label="ID в БД" value={diagnostics.user.databaseId} />
              <Row label="язык из Telegram" value={telegramLanguage} />
              <Row label="язык в БД" value={diagnostics.user.savedLanguage} />
              <Row label="язык после нормализации" value={diagnostics.user.normalizedLanguage} />
            </Section>

            <Section title="Определение страны">
              <Row label="req.ip" value={geo?.requestIp} />
              <Row label="x-forwarded-for" value={geo?.forwardedFor} />
              {Object.entries(geo?.proxyCountryHeaders || {}).map(([name, value]) => (
                <Row key={name} label={name} value={value} />
              ))}
              <Row label="итоговая страна" value={geo?.country} color={geo?.country ? '#93c5fd' : '#f87171'} />
              <Row label="источник страны" value={geo?.source} />
              <Row label="город из локальной базы" value={geo?.city} />
              <Row label="регион из локальной базы" value={geo?.region} />
              <Row label="часовой пояс" value={geo?.timezone} />
              <Row label="время определения, мс" value={geo?.lookupMs} />
              <Row label="ошибка геолокации" value={geo?.error} color={geo?.error ? '#f87171' : undefined} />
              {(geo?.attempts || []).map((attempt) => (
                <Row
                  key={attempt.provider}
                  label={`попытка ${attempt.provider}`}
                  value={`HTTP ${attempt.httpStatus ?? '—'}, страна ${attempt.country || '—'}, ${attempt.lookupMs} мс${attempt.error ? `, ошибка ${attempt.error}` : ''}`}
                  color={attempt.country ? '#4ade80' : '#f87171'}
                />
              ))}
            </Section>

            <Section title="Итог RU-фильтра">
              <Row label="категория" value={target ? 'RU / VPN-аудитория' : 'обычный пользователь'} color={target ? '#4ade80' : '#fbbf24'} />
              <Row label="код категории" value={classification?.category} />
              <Row label="eligible" value={classification?.eligible} />
              <Row label="в списке СНГ-исключений" value={classification?.isCisCountry} />
              <Row label="ru вне СНГ считается целевым" value={classification?.ruOutsideCisTarget} />
              <Row label="причина" value={classification?.reasonLabel} />
              <Row label="код причины" value={classification?.reason} />
              <Row label="есть следующая спецссылка" value={diagnostics.available} />
              <Row label="следующая спецссылка" value={diagnostics.nextSystemLink ? `№${diagnostics.nextSystemLink.priority} (ID ${diagnostics.nextSystemLink.id})` : null} />
            </Section>

            <div style={{ color: '#9ca3af', margin: '12px 0 6px' }}>Системные ссылки</div>
            {diagnostics.systemLinks.map((link) => (
              <div key={link.priority} style={{ padding: '8px', border: '1px solid #1f2937', borderRadius: 6, marginBottom: 8 }}>
                <div style={{ color: link.available ? '#4ade80' : '#9ca3af', marginBottom: 5 }}>
                  Системная №{link.priority}: {link.available ? 'доступна' : 'не выдаётся'}
                </div>
                <div style={gridStyle}>
                  <Row label="ID" value={link.id} />
                  <Row label="URL" value={link.url} />
                  <Row label="включена" value={link.active} />
                  <Row label="уже засчитана" value={link.alreadyUsed} />
                  <Row label="доступна сейчас" value={link.available} />
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(120px, auto) 1fr',
  gap: '3px 10px',
};

const buttonStyle: React.CSSProperties = {
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #374151',
  background: '#111827',
  color: '#e5e7eb',
  fontSize: 11,
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section style={{ marginBottom: 12 }}>
    <div style={{ color: '#9ca3af', marginBottom: 6 }}>{title}</div>
    <div style={gridStyle}>{children}</div>
  </section>
);

export default AdDebugPanel;
